import { getWeek } from "helpers/functions";
import { PriceTier } from "./interface";

export interface PriceBreakdown {
  tier: string;
  week: number;
  base: number;
  thursdaySurcharge: number;
}

/**
 * Finds the price for a company presentation on the given date from the season's price tiers.
 * Returns undefined if the date's week is not in any tier. Mirrors BookingSeason.price_for_date in the backend.
 */
export const getPriceForDate = (tiers: PriceTier[], date: Date): PriceBreakdown | undefined => {
  const week = getWeek(date);
  const tier = tiers.find(t => t.weeks.includes(week));
  if (!tier) return undefined;
  return {
    tier: tier.name,
    week,
    base: tier.price,
    thursdaySurcharge: date.getDay() === 4 ? tier.thursday_surcharge : 0,
  };
};

export const formatAmount = (amount: number) => `${amount.toLocaleString("no")} kr`;

export const formatPrice = (amount: number) => `${formatAmount(amount)} ekskl. mva`;

/**
 * Formats week numbers with consecutive weeks grouped, e.g. [6, 7, 8, 11] -> "uke 6–8, 11".
 */
export const formatWeeks = (weeks: number[]): string => {
  const sorted = [...weeks].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  sorted.forEach((week, i) => {
    const next = sorted[i + 1];
    if (next === week + 1) return;
    ranges.push(start === week ? `${week}` : `${start}–${week}`);
    start = next;
  });
  return ranges.length ? `uke ${ranges.join(", ")}` : "";
};

/**
 * The ISO weeks from semester start to semester end, in order. Mirrors BookingSeason.semester_weeks in the backend.
 */
export const getSemesterWeeks = (start: Date, end: Date): number[] => {
  const weeks: number[] = [];
  const day = new Date(start);
  while (day <= end) {
    const week = getWeek(day);
    if (!weeks.includes(week)) weeks.push(week);
    day.setDate(day.getDate() + 1);
  }
  return weeks;
};
